import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { ConfirmationOverlay } from "../../../../components/common/ConfirmationOverlay";
import { BackendErrorMessage } from "../../../../components/common/ErrorMessage";

import Head from "next/head";
import React from "react";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { TeamImage } from "../../../../components/common/Images";
import { Nav } from "../../../../components/common/Nav";
import { NoItems } from "../../../../components/common/NoItems";
import { QueryWrapper } from "../../../../components/common/QueryWrapper";
import { useUrl } from "../../../../hooks/common/useUrl";
import { useGetOutfitters } from "../../../../hooks/outfitter";
import { useGetSeasons } from "../../../../hooks/season";
import { useDeleteTeam, useGetTeams } from "../../../../hooks/teams";
import { useGetVenues } from "../../../../hooks/venue";
import { SeasonTeam } from "../../../../models/Team";

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export function AddTeamsView() {
  const { t } = useTranslation();
  const { customUrl } = useUrl();

  const { data: venues } = useGetVenues();
  const { data: outfitters } = useGetOutfitters();

  return (
    <React.Fragment>
      {venues && venues.length > 0 && outfitters && outfitters.length > 0 && (
        <Link href={customUrl("/teams/add")} className="btn uppercase mb-8">
          <FiPlus className="mr-2" /> {t("Add_Team")}
        </Link>
      )}
    </React.Fragment>
  );
}

export default function TeamsPage() {
  const { seasonId } = useUrl();

  const { ...seasonsResult } = useGetSeasons();
  const { ...teamsResult } = useGetTeams({
    seasonId,
  });
  const { ...venuesResult } = useGetVenues();
  const { ...outfittersResult } = useGetOutfitters();

  return (
    <>
      <Head>
        <title>TTBL Adminsystem | Teams</title>
      </Head>
      <QueryWrapper
        loaderType="big"
        useQueryResult={[
          teamsResult,
          seasonsResult,
          outfittersResult,
          venuesResult,
        ]}
      >
        <div>
          <Nav />
          <div className="container">
            <div className="rounded-b-lg  py-8 mb-10">
              <TeamsView seasonId={seasonId} />
            </div>
          </div>
        </div>
      </QueryWrapper>
    </>
  );
}

interface TeamsViewProps {
  seasonId: string;
}

function TeamsView({ seasonId }: TeamsViewProps) {
  const { data: teams, ...teamsResult } = useGetTeams({
    seasonId,
  });
  const { t } = useTranslation();
  return (
    <QueryWrapper useQueryResult={[teamsResult]}>
      {teams && teams.length > 0 && (
        <table>
          <tbody>
            <tr className="table-header row_item">
              <th>{t("Label_Name")}</th>
              <th>{t("Label_Shortname")}</th>
              <th className="text-right">{t("Label_Options")}</th>
            </tr>
            {teams.map((team) => (
              <TeamCard key={team.id} team={team} />
            ))}
          </tbody>
        </table>
      )}
      {teams && teams.length === 0 && <NoItems />}
    </QueryWrapper>
  );
}
interface TeamCardProps {
  team: SeasonTeam;
}
function TeamCard({ team }: TeamCardProps) {
  const { customUrl } = useUrl();
  const { t } = useTranslation();
  const {
    mutate: deleteTeam,
    error,
    ...isTeamLoading
  } = useDeleteTeam({ teamId: team.id });

  return (
    <tr className="row_item">
      <td>
        <Link
          className="flex w-full"
          href={customUrl(`/teams/edit/${team.id}`)}
        >
          <div className="flex gap-4">
            <div>
              <TeamImage team={team} />
            </div>
            <div className="ml-4">{team.name}</div>
          </div>
        </Link>
      </td>
      <td>
        <Link
          className="flex w-full"
          href={customUrl(`/teams/edit/${team.id}`)}
        >
          {team.shortname}
        </Link>
      </td>
      <td className="">
        <div className="flex justify-end gap-2">
          <div>
            <Link
              className="ml-auto mr-2"
              href={customUrl(`/teams/edit/${team.id}`)}
            >
              <FiEdit />
            </Link>
          </div>
          <div>
            <ConfirmationOverlay
              isSubmitting={isTeamLoading.isLoading}
              onClick={() => deleteTeam()}
              confirmationMessage={t("Confirmation_Delete") as string}
            >
              <FiTrash2 />
            </ConfirmationOverlay>
          </div>
          <BackendErrorMessage error={error} />
        </div>
      </td>
    </tr>
  );
}
